import { VersionConfig } from '@/types/assessment';

export const highSchoolConfig: VersionConfig = {
  edition: 'highschool',
  label: 'High School Edition',
  tagline: 'Your future doesn\'t have to look like anyone else\'s.',
  pathNames: { conservative: 'College Prep Focus', moderate: 'College + Career Discovery', bold: 'Start Your Own Thing' },
  timelines: ['This Semester', 'Next Year', 'After Graduation'],
  socialPlatforms: ['Instagram', 'TikTok', 'YouTube'],
  screens: [
    {
      id: 'skills',
      title: 'How does your brain work?',
      subtitle: 'Pick up to 4 things that feel most like you.',
      encouragement: 'There are no wrong answers, just patterns worth noticing.',
      type: 'chips-expandable',
      maxSelections: 4,
      options: [
        { id: 'analytical', label: 'Breaking Things Down', description: 'You like figuring out how things work', looksLike: 'You love puzzles, fixing things, finding patterns, or figuring out "why"' },
        { id: 'creative', label: 'Coming Up with Ideas', description: 'You imagine things that don\'t exist yet', looksLike: 'You sketch, write stories, or think of solutions nobody else sees' },
        { id: 'communication', label: 'Explaining Things Clearly', description: 'You make confusing things make sense', looksLike: 'Friends come to you when they don\'t get something, you write well, you present well' },
        { id: 'leadership', label: 'Taking Charge', description: 'You step up when nobody else does', looksLike: 'You organize group projects, captain teams, or rally people around ideas' },
        { id: 'tech-savvy', label: 'Picking Up Tech Fast', description: 'You figure out new apps and tools quickly', looksLike: 'You\'re the one who sets up the group\'s tech, builds spreadsheets, or learns new software in a day' },
        { id: 'empathy', label: 'Understanding People', description: 'You notice how others feel', looksLike: 'Friends come to you for advice, you notice when someone\'s having a bad day, you care deeply' },
        { id: 'adaptability', label: 'Going with the Flow', description: 'You handle change without stressing', looksLike: 'Plans change? No problem. You adjust, improvise, and keep going' },
        { id: 'research', label: 'Going Deep on Topics', description: 'You dig until you find the answer', looksLike: 'You go deep on Wikipedia, watch 3-hour documentaries, or read everything about a topic' },
        { id: 'design', label: 'Making Things Look & Work Better', description: 'You care about how things are designed', looksLike: 'You notice when an app is ugly or confusing, you rearrange your room, you care about aesthetics' },
        { id: 'collaboration', label: 'Working with Others', description: 'You\'re better with a team around you', looksLike: 'Group projects? You\'re in. You build on others\' ideas and make teams click' },
        { id: 'organizing', label: 'Keeping Things Organized', description: 'You like planning and keeping things in order', looksLike: 'Color-coded notes, neat folders, you\'re the one with the plan' },
        { id: 'hands-on', label: 'Building with Your Hands', description: 'You like making physical things', looksLike: 'You build, fix, cook, craft, or tinker with things in the real world' },
      ],
    },
    {
      id: 'strengths',
      title: 'What makes you, you?',
      subtitle: 'Choose up to 5 strengths you\'re proud of.',
      encouragement: 'Own it. These are the things that set you apart.',
      type: 'chips-expandable',
      maxSelections: 5,
      options: [
        { id: 'pattern-recognition', label: 'Spotting Connections', description: 'You see links between things others miss', looksLike: 'You predict what\'s going to happen, catch mistakes fast, or connect random dots' },
        { id: 'storytelling', label: 'Telling Stories', description: 'You make ideas stick through stories', looksLike: 'Your presentations are interesting, your writing flows, you hook people in' },
        { id: 'resilience', label: 'Bouncing Back', description: 'When things go wrong, you get back up', looksLike: 'Failed a test? Got rejected? You dust off and try again, harder' },
        { id: 'curiosity', label: 'Always Asking "Why?"', description: 'You never stop wanting to learn', looksLike: '27 browser tabs open, random hobby deep-dives, you just need to know' },
        { id: 'hustle', label: 'Getting It Done', description: 'You make things happen, fast', looksLike: 'Side projects, early mornings, "I\'ll figure it out" energy' },
        { id: 'vision', label: 'Seeing the Big Picture', description: 'You think ahead and plan big', looksLike: 'You think 5 steps ahead, set ambitious goals, dream in possibilities' },
        { id: 'calm-under-pressure', label: 'Staying Calm When It\'s Crazy', description: 'You stay cool when things heat up', looksLike: 'Deadlines, drama, chaos: you\'re the steady one everyone looks to' },
        { id: 'connector', label: 'Bringing People Together', description: 'You naturally connect the right people', looksLike: 'You introduce friends, build groups, know everyone, create communities' },
        { id: 'self-starter', label: 'Starting Without Being Told', description: 'You don\'t wait for permission to begin', looksLike: 'You start projects on your own, teach yourself things, don\'t need someone pushing you' },
        { id: 'detail-oriented', label: 'Noticing the Small Stuff', description: 'You catch details others walk right past', looksLike: 'Typos jump out at you, you spot when something\'s off, your work is precise' },
      ],
    },
    {
      id: 'interests',
      title: 'What gets you excited?',
      subtitle: 'Pick as many as you want. No limits.',
      encouragement: 'Follow the energy. What would you do even if nobody was watching?',
      type: 'chips',
      options: [
        { id: 'ai-tech', label: 'AI & Computers' },
        { id: 'arts', label: 'Art & Design' },
        { id: 'business', label: 'Starting a Business' },
        { id: 'science', label: 'Science & Experiments' },
        { id: 'helping-people', label: 'Helping People' },
        { id: 'health', label: 'Health & The Body' },
        { id: 'media', label: 'Making Videos & Content' },
        { id: 'teaching', label: 'Teaching & Tutoring' },
        { id: 'sports', label: 'Sports & Fitness' },
        { id: 'music', label: 'Music & Performing' },
        { id: 'environment', label: 'Protecting the Planet' },
        { id: 'gaming', label: 'Gaming & Esports' },
        { id: 'fashion', label: 'Fashion & Style' },
        { id: 'cooking', label: 'Food & Cooking' },
        { id: 'writing', label: 'Writing & Journalism' },
        { id: 'psychology', label: 'Understanding How People Think' },
        { id: 'animals', label: 'Animals & Veterinary' },
        { id: 'law', label: 'Law & Debate' },
        { id: 'building', label: 'Building & Engineering' },
        { id: 'money', label: 'Investing & Money' },
      ],
    },
    {
      id: 'aspirations',
      title: 'What kind of future sounds cool?',
      subtitle: 'Pick up to 3 areas you\'re curious about. Don\'t overthink it.',
      type: 'aspirations',
      maxSelections: 3,
      options: [
        { id: 'ai-ml', label: 'AI & Robots' },
        { id: 'creator-economy', label: 'Being a Creator (YouTube, TikTok, etc.)' },
        { id: 'climate-tech', label: 'Saving the Planet with Tech' },
        { id: 'healthcare-ai', label: 'Medicine & Healthcare' },
        { id: 'fintech', label: 'Money, Apps & Banking Tech' },
        { id: 'edtech', label: 'Teaching with Technology' },
        { id: 'gaming-vr', label: 'Video Games & Virtual Reality' },
        { id: 'social-enterprise', label: 'Running a Business that Helps People' },
        { id: 'space', label: 'Space & Aerospace' },
        { id: 'cybersecurity', label: 'Cybersecurity & Hacking (the good kind)' },
        { id: 'sports-science', label: 'Sports Science & Athletics' },
        { id: 'entertainment', label: 'Film, TV & Entertainment' },
        { id: 'architecture', label: 'Architecture & City Design' },
        { id: 'law-policy', label: 'Law, Government & Policy' },
        { id: 'biotech', label: 'Biotech & Genetics' },
      ],
    },
    {
      id: 'energy-workstyle',
      title: 'What\'s your energy like?',
      subtitle: 'How do you recharge, and how do you like to work?',
      encouragement: 'Understanding your energy is the cheat code to not burning out.',
      type: 'chips-expandable',
      maxSelections: 5,
      options: [
        { id: 'deep-focus', label: 'Deep Focus', description: 'You do your best work in long, uninterrupted blocks', looksLike: 'Headphones on, phone off, in the zone for hours' },
        { id: 'burst-energy', label: 'Quick Bursts', description: 'You work in intense sprints then need a break', looksLike: 'Crush it for 2 hours, then need a walk and a snack' },
        { id: 'social-energy', label: 'People Energy', description: 'You get fired up working with others', looksLike: 'Brainstorms, group chats, study groups — the more people, the better' },
        { id: 'solo-creator', label: 'Solo Mode', description: 'You create best when you\'re alone', looksLike: 'Late night sessions, solo projects, your own playlist blasting' },
        { id: 'variety-seeker', label: 'Need to Switch It Up', description: 'You get bored doing the same thing too long', looksLike: 'Three hobbies, two side projects, and a new interest every month' },
        { id: 'steady-builder', label: 'Slow & Steady', description: 'You prefer consistent, day-by-day progress', looksLike: 'Daily practice, routines, small wins that add up over time' },
        { id: 'night-owl', label: 'Night Owl', description: 'Your best ideas come after midnight', looksLike: 'Late-night creating, writing at 2am, sleeping through first period' },
        { id: 'morning-person', label: 'Early Bird', description: 'You\'re sharpest before noon', looksLike: '6am runs, early study sessions, most productive in the morning' },
        { id: 'multitasker', label: 'Doing Multiple Things at Once', description: 'You like having several things going on', looksLike: 'Music while studying, texting while eating, always juggling stuff' },
      ],
    },
    {
      id: 'dream-wednesday',
      title: 'Picture your dream Wednesday',
      subtitle: 'Not a vacation day — a regular work day. What does it look like?',
      encouragement: 'This isn\'t about being realistic. It\'s about being honest.',
      type: 'dream-wednesday',
      vibeOptions: [
        { id: 'ceo-vibes', label: 'The Boss', description: 'Making decisions, leading a team, running the show', pros: ['You call the shots', 'Big impact', 'People look to you'], cons: ['Always busy', 'Lots of responsibility', 'Less creative time'] },
        { id: 'creator-vibes', label: 'The Creator', description: 'Making things all day: designing, writing, building', pros: ['Creative freedom', 'You make cool stuff', 'You can get in the zone'], cons: ['Income can be unpredictable', 'Need self-discipline', 'Can feel lonely'] },
        { id: 'explorer-vibes', label: 'The Explorer', description: 'New places, new people, new ideas every day', pros: ['Never boring', 'Huge network', 'Always learning'], cons: ['Less routine', 'Harder to go deep', 'Can be tiring'] },
        { id: 'helper-vibes', label: 'The Helper', description: 'Teaching, mentoring, making someone\'s life better', pros: ['Feels really meaningful', 'Strong community', 'Real impact on people'], cons: ['Can be emotionally hard', 'Sometimes underpaid', 'Burnout risk'] },
        { id: 'builder-vibes', label: 'The Builder', description: 'Starting something from scratch and watching it grow', pros: ['You own what you build', 'Real results you can see', 'Always learning'], cons: ['Can be lonely at first', 'Lots of failures before wins', 'Long hours'] },
        { id: 'analyst-vibes', label: 'The Detective', description: 'Finding answers in data, solving complex puzzles', pros: ['Companies need you', 'Clear right and wrong answers', 'Brain-stimulating work'], cons: ['Lots of screen time', 'Can feel repetitive', 'Less people interaction'] },
      ],
      allowOpenText: true,
      openTextPlaceholder: 'Describe your ideal Wednesday in a few sentences...',
    },
    {
      id: 'values',
      title: 'What matters most to you?',
      subtitle: 'Pick up to 5 things you\'d never want to give up.',
      encouragement: 'These aren\'t just words. They\'re your compass.',
      type: 'chips',
      maxSelections: 5,
      options: [
        { id: 'freedom', label: 'Being My Own Boss' },
        { id: 'security', label: 'Making Good Money' },
        { id: 'creativity', label: 'Being Creative' },
        { id: 'impact', label: 'Making a Difference' },
        { id: 'community', label: 'Belonging to a Community' },
        { id: 'growth', label: 'Always Getting Better' },
        { id: 'justice', label: 'Standing Up for What\'s Right' },
        { id: 'innovation', label: 'Trying New Things' },
        { id: 'balance', label: 'Having Time for Life Outside Work' },
        { id: 'recognition', label: 'Being Recognized for My Work' },
        { id: 'adventure', label: 'Travel & New Experiences' },
        { id: 'family', label: 'Family & Close Relationships' },
        { id: 'stability', label: 'Knowing What to Expect' },
      ],
    },
    {
      id: 'cognitive-style',
      title: 'How do you think?',
      subtitle: 'Quick scenarios — just go with your gut.',
      encouragement: 'There are no right or wrong answers. Just your natural instincts.',
      type: 'cognitive-style',
      cognitiveScenarios: [
        {
          id: 'cs-1',
          scenario: 'You\'re assigned a group project with a tight deadline. The team is stuck on what direction to take.',
          optionA: { label: 'I\'d research what similar teams have done and present the data to the group', trait: 'Analytical' },
          optionB: { label: 'I\'d brainstorm 5 wild ideas on a whiteboard and let the group vote', trait: 'Creative' },
        },
        {
          id: 'cs-2',
          scenario: 'You just learned something mind-blowing in class. What do you do next?',
          optionA: { label: 'I deep-dive into articles and videos to fully understand it', trait: 'Deep Learner' },
          optionB: { label: 'I immediately try to explain it to a friend or post about it', trait: 'Social Processor' },
        },
        {
          id: 'cs-3',
          scenario: 'A new app idea pops into your head. How do you approach it?',
          optionA: { label: 'I map out features, how it would work, and potential problems first', trait: 'Systematic' },
          optionB: { label: 'I start building a rough version immediately to see if it feels right', trait: 'Intuitive' },
        },
        {
          id: 'cs-4',
          scenario: 'You\'re choosing between two summer opportunities: one is safe and structured, the other is exciting but uncertain.',
          optionA: { label: 'I\'d go with the structured one. I can always explore later', trait: 'Risk-Cautious' },
          optionB: { label: 'I\'d take the uncertain one. I learn more when I\'m uncomfortable', trait: 'Risk-Seeking' },
        },
      ],
    },
    {
      id: 'experience',
      title: 'Any experience so far?',
      subtitle: 'Totally optional. If you have work, volunteer, or project experience, share it here.',
      encouragement: 'Even small experiences count. Babysitting, volunteering, helping at a family business — it all matters.',
      type: 'context',
      options: [],
      allowOpenText: true,
      openTextPlaceholder: 'e.g., Volunteered at food bank for 2 years, part-time at local coffee shop, built a website for my school club...',
    },
    {
      id: 'personalize',
      title: 'Last thing — what does your future look like?',
      subtitle: 'These answers help us match you with paths that actually fit your life.',
      encouragement: 'No wrong answers. Just be honest about what you want.',
      type: 'personalize',
    },
  ],
};
